/**
 * A lista de arquivos de uma subpasta da cliente, com "Abrir" e "Excluir", idêntica à do legado
 * (`listarArquivosNaPasta` e `criarLinhaArquivoAdmin`, `admin.html:6344-6395`; linhas 195, 197 e
 * 212 do inventário). Usada pela aba Arquivos (duas vezes) e pelo Dossiê.
 *
 * O QUE ELA REPRODUZ, e cada ponto é do legado:
 *   · percorre as pastas na ordem recebida (a do lead e a antiga), e uma pasta que falha ou vem
 *     vazia é pulada em silêncio (`6388`);
 *   · cada arquivo ganha um link assinado de uma hora; se o link falhar, o "Abrir" aponta para `#`;
 *   · a lista ESVAZIA a cada leitura e as linhas voltam uma a uma, quando o link de cada arquivo
 *     chega (`el.innerHTML = ''` em `6385` e o `appendChild` de `6392`);
 *   · sem nenhum arquivo em nenhuma pasta, "Nenhum arquivo ainda." (`6394`);
 *   · "Excluir" é um link VERMELHO (`a.excluir-link`, `admin.html:6360-6362` e o CSS de `480`),
 *     confirma com `Excluir o arquivo "nome"?`, apaga, e em erro avisa "Erro ao excluir: " com a
 *     mensagem crua (`6366-6368`); depois relê as pastas.
 */

// Estado, efeito de layout e referência do React.
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
// As chamadas ao Storage da ficha.
import { excluirArquivo, gerarLinkAssinado, listarPasta } from '@studiolu/dados/areas/ficha-arquivos';
// A fonte de dados do painel.
import { usePainel } from '../../../../contexto.js';
// A confirmação e o aviso do legado, publicados em `comum/`.
import { useConfirmacao } from '../../../../comum/confirmacao.js';
// O recorte por papel das abas da A3.
import { useRecortePorPapel } from './papel.js';

/** Uma linha da lista: a pasta, o nome e o link assinado (ou `null` quando ele falhou). */
interface LinhaDeArquivo {
  readonly pasta: string;
  readonly nome: string;
  readonly link: string | null;
}

/**
 * A ligação que deixa a ABA comandar a leitura desta lista.
 *
 * POR QUE A LISTA NÃO LÊ SOZINHA: no legado quem lê é o carregador da aba (`carregarArquivos`,
 * `admin.html:6411-6414`, e `carregarDossiePrevias`, `5570`), e a moldura espera esse carregador
 * terminar ANTES de mostrar a aba (`5086-5091`). Com a lista lendo por conta própria, a aba
 * apareceria pronta com a lista ainda vazia, que é o defeito T1 da verificação.
 */
export interface LigacaoDaLista {
  ler: () => Promise<void>;
}

/** Cria a ligação vazia que a aba guarda numa referência. */
export function ligacaoVazia(): LigacaoDaLista {
  return { ler: async () => undefined };
}

export interface ListaDeArquivosProps {
  // As pastas a percorrer, na ordem do legado (`pastasDaSubpasta`).
  readonly pastas: readonly string[];
  // Por onde a aba dispara a leitura desta lista.
  readonly ligacao: LigacaoDaLista;
  // O identificador da lista na suíte.
  readonly testid: string;
}

export function ListaDeArquivos({ pastas, ligacao, testid }: ListaDeArquivosProps) {
  const { dados } = usePainel();
  const { veLinksDeAdministrador } = useRecortePorPapel();
  const { confirmar, alertar, dialogo } = useConfirmacao();
  // O que está na tela: as linhas já desenhadas e se a leitura terminou. Enquanto ela não termina,
  // a área fica vazia SEM a frase de vazio, que é o que o legado mostra entre o `innerHTML = ''` e
  // o fim do laço (`admin.html:6385-6394`).
  const [leitura, setLeitura] = useState<{ readonly linhas: readonly LinhaDeArquivo[]; readonly terminou: boolean }>({
    linhas: [],
    terminou: false,
  });
  // Cada leitura ganha um número; só a mais recente pode escrever na tela, para uma resposta
  // atrasada de uma pasta antiga não sobrescrever a lista de outra cliente.
  const leituraAtual = useRef(0);
  // As pastas viram texto para o efeito comparar por valor, e não pela identidade da lista.
  const chaveDasPastas = pastas.join('|');

  // A leitura das pastas, em sequência como os `await` do legado, para a ordem das linhas ser a mesma.
  const ler = useCallback(async () => {
    const minha = ++leituraAtual.current;
    // DEFEITO DO LEGADO REPRODUZIDO: a lista some ANTES de a leitura começar (`el.innerHTML = ''`,
    // `admin.html:6385`), e a área fica vazia enquanto o Storage responde. Manter as linhas
    // anteriores seria mais bonito e seria outra tela.
    setLeitura({ linhas: [], terminou: false });
    const encontradas: LinhaDeArquivo[] = [];
    for (const pasta of chaveDasPastas.split('|')) {
      const lista = await listarPasta(dados, pasta);
      if (minha !== leituraAtual.current) return;
      // Falha ou pasta vazia: pula, como o `continue` de `admin.html:6388`.
      if (!lista.ok || !lista.dados || lista.dados.length === 0) continue;
      for (const arquivo of lista.dados) {
        // O link de cada arquivo, pedido um a um como no legado (`6345`).
        const link = await gerarLinkAssinado(dados, `${pasta}/${arquivo.name}`);
        if (minha !== leituraAtual.current) return;
        encontradas.push({ pasta, nome: arquivo.name, link: link.ok && link.dados ? link.dados.signedUrl : null });
        // Cada linha aparece quando o link dela volta, como o `appendChild` de `6392`.
        setLeitura({ linhas: [...encontradas], terminou: false });
      }
    }
    // Só a leitura mais recente escreve o fim, que é quem libera a frase de vazio.
    if (minha === leituraAtual.current) setLeitura({ linhas: encontradas, terminou: true });
  }, [dados, chaveDasPastas]);

  // Entrega a leitura à aba. É EFEITO DE LAYOUT porque o efeito de layout do filho roda antes do
  // efeito do pai: quando a aba pedir a carga, a ligação já aponta para a leitura desta lista.
  useLayoutEffect(() => {
    ligacao.ler = ler;
  }, [ligacao, ler]);

  // "Excluir": confirma, apaga e relê (`admin.html:6364-6370`).
  const excluir = async (linha: LinhaDeArquivo) => {
    if (!(await confirmar(`Excluir o arquivo "${linha.nome}"?`))) return;
    const resultado = await excluirArquivo(dados, `${linha.pasta}/${linha.nome}`);
    // O legado mostra a mensagem crua do Storage depois do prefixo, e não a tradução.
    if (!resultado.ok) {
      await alertar('Erro ao excluir: ' + resultado.erro.mensagem);
      return;
    }
    await ler();
  };

  return (
    <div data-testid={testid} className="flex flex-col gap-cx-1">
      {leitura.terminou && leitura.linhas.length === 0 && (
        <p className="text-cx-sm text-texto-suave">Nenhum arquivo ainda.</p>
      )}
      {leitura.linhas.map((linha) => (
        <div
          key={`${linha.pasta}/${linha.nome}`}
          data-testid="arquivo-linha"
          className="flex flex-wrap items-center justify-between gap-cx-2 border-b border-borda py-cx-1"
        >
          {/* O nome quebra em qualquer ponto, para um nome longo não estourar a largura no celular. */}
          <span className="min-w-0 break-all text-cx-sm text-texto">{linha.nome}</span>
          <span className="flex shrink-0 items-center gap-cx-2">
            {/* Link que falhou vira `#`, e `a[href="#"]` some para quem não é administrador (`3375`). */}
            {(linha.link !== null || veLinksDeAdministrador) && (
              // O "Abrir" do legado não tem classe: vale o `a` da página, que é a cor do texto
              // (`admin.html:173`) com o sublinhado que o navegador desenha.
              <a
                href={linha.link ?? '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex min-h-toque items-center text-cx-sm text-texto underline underline-offset-4"
              >
                Abrir
              </a>
            )}
            {/* "Excluir" é só do administrador (`admin.html:3384-3389`) e é VERMELHO, como a
                `.excluir-link` do legado (`480`) e como a lista de arquivos da A1. Decisão do
                fundador de 19/09/2026: toda ação de apagar é vermelha em todo lugar. Ele é um
                link com `href="#"`, e não um botão, porque é assim que o legado o desenha, e é o
                `a[href="#"]` que some para quem não é administrador (`3375`). */}
            {veLinksDeAdministrador && (
              <a
                href="#"
                className="inline-flex min-h-toque items-center text-cx-sm text-erro underline underline-offset-4"
                onClick={(evento) => {
                  // O `#` não navega: é o `e.preventDefault()` do legado (`6365`).
                  evento.preventDefault();
                  void excluir(linha);
                }}
              >
                Excluir
              </a>
            )}
          </span>
        </div>
      ))}
      {dialogo}
    </div>
  );
}
